<template>
  <div class="categories-list">
    <div class="flex flex-wrap items-center justify-between">
      <div
        class="view-toggle mb-4 flex w-fit flex-wrap items-center gap-2 rounded-xl border border-solid border-gray-200 bg-white p-1"
        data-cy="categories-view-toggle"
      >
        <OurButton
          size="sm"
          data-cy="categories-view-table"
          :variant="isTableView ? 'primary' : 'thirdary'"
          :filled="!isTableView"
          :text="$t('categories.tree.modeTable')"
          @click="setViewMode('table')"
        />

        <OurButton
          size="sm"
          data-cy="categories-view-tree"
          :variant="isTreeView ? 'primary' : 'thirdary'"
          :filled="!isTreeView"
          :text="$t('categories.tree.modeTree')"
          @click="setViewMode('tree')"
        />
      </div>
      <TotalInfoBlock v-if="paginationData?.total" :total="parseInt(paginationData?.total)" />
    </div>

    <template v-if="isTreeView">
      <div class="categories-tree">
        <div class="tree-card rounded-xl border border-solid border-gray-200 bg-white">
          <div
            class="toolbar flex flex-wrap items-center gap-3 border-b border-solid border-gray-100 px-4 py-3"
          >
            <OurButton
              variant="thirdary"
              filled
              :text="$t('categories.tree.expandAll')"
              data-cy="categories-tree-expand-all"
              @click="expandAllBranches"
            />

            <OurButton
              variant="thirdary"
              filled
              :text="$t('categories.tree.collapseAll')"
              data-cy="categories-tree-collapse-all"
              @click="collapseAllBranches"
            />
          </div>

          <div class="tree-card-body px-4 pb-2 pt-1">
            <OurTableList
              ref="categoriesTreeTable"
              nesting
              collapsible-nesting
              data-cy="categories-tree-table"
              :group="dragGroupName"
              :have-permission-delete="getPermission['catalog_category.delete']"
              :have-permission-edit="getPermission['catalog_category.update']"
              :is-draggable="getPermission['catalog_category.update']"
              :list="localCategoriesTree"
              @clickEdit="openCategory"
              @dragSort="onDragSort"
              @clickDelete="onDeleteCategory"
            >
              <template #default="{ item }">
                <div
                  class="tree-row-label list-item-left-title cursor-pointer text-base leading-snug text-gray-900"
                  role="button"
                  tabindex="0"
                  @click="openCategory(item.id)"
                >
                  {{ item.name }}
                </div>
              </template>
            </OurTableList>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <template v-if="isMobileDevice">
        <div v-if="items.length" class="flex flex-col gap-3">
          <div
            v-for="item of items"
            :key="item.id"
            class="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            @click="onClickTableRow(item)"
          >
            <div class="font-medium">{{ item.name }}</div>
            <OurTag class="mt-2" size="md" :text="item.status?.text" :color="item.status?.color" />
          </div>
        </div>
        <div v-else>{{ $t("page.dashboard.emptyMessage") }}</div>
      </template>

      <OurTable
        v-else
        :headers="resolvedTableHeaders"
        :items="items"
        :table-fixed="false"
      >
        <template #cell-status="{ value }">
          <OurTag size="md" :text="value?.text" :color="value?.color" />
        </template>
        <template #cell-parent="{ value }">
          {{ value?.name }}
        </template>
      </OurTable>

      <OurPagination
        v-if="isMoreThanOnePage"
        :current-page="paginationData.currentPage"
        :per-page="paginationData.perPage"
        :total="paginationData.total"
        :disabled="isLoading"
        @pageChange="onPageChange"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import apolloMixin from "@/mixins/apollo.mixin";
import { moveCatalogCategoryMutation } from "@/views/settings/categories/index/gql/mutations";
import TotalInfoBlock from "@/components/TotalInfoBlock.vue";
import { buildCategoriesListTableHeaders } from "@/views/settings/categories/index/config/categoriesListTableHeaders";

const VIEW_TABLE = "table";
const VIEW_TREE = "tree";

function compareCategorySort(a, b) {
  return Number(a.sort) - Number(b.sort);
}

function resolveMoveCatalogCategoryPayload(items, treeRoots) {
  const prevParent = new Map();
  const prevRowsByParent = new Map();

  for (const row of items) {
    const p = row.parent?.id ?? null;

    prevParent.set(row.id, p);

    if (!prevRowsByParent.has(p)) prevRowsByParent.set(p, []);

    prevRowsByParent.get(p).push(row);
  }

  for (const list of prevRowsByParent.values()) {
    list.sort((a, b) => Number(a.sort) - Number(b.sort));
  }

  const prevOrderByParent = new Map(
    [...prevRowsByParent].map(([p, rows]) => [p, rows.map((r) => r.id)])
  );

  const nextParent = new Map();
  const nextOrderByParent = new Map();

  const walk = (nodes, p = null) => {
    nextOrderByParent.set(
      p,
      nodes.map((n) => n.id)
    );

    for (const n of nodes) {
      nextParent.set(n.id, p);

      if (n.children?.length) walk(n.children, n.id);
    }
  };

  walk(Array.isArray(treeRoots) ? treeRoots : []);

  for (const [id, p] of nextParent) {
    if (prevParent.get(id) !== p) {
      return { id, newParentId: p, orderedIds: nextOrderByParent.get(p) ?? [] };
    }
  }

  for (const [p, order] of nextOrderByParent) {
    const prev = prevOrderByParent.get(p) ?? [];

    if (
      !prev.length ||
      prev.length !== order.length ||
      [...prev].sort().join("|") !== [...order].sort().join("|") ||
      !prev.some((nid, idx) => nid !== order[idx])
    ) {
      continue;
    }

    const i = order.findIndex((nid, idx) => prev[idx] !== nid);

    return { id: order[i === -1 ? 0 : i], newParentId: p, orderedIds: order };
  }

  return null;
}

export default {
  name: "CategoriesList",
  components: { TotalInfoBlock },
  mixins: [apolloMixin],

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    paginationData: {
      type: Object,
      default: () => ({}),
    },

    treeItems: {
      type: Array,
      default: () => [],
    },

    treeFilters: {
      type: Object,
      default: () => ({}),
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    headers: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      viewMode: VIEW_TABLE,
      dragGroupName: "settings-categories-tree",
      localCategoriesTree: [],
      pageChangeResolve: null,
    };
  },

  computed: {
    ...mapGetters("breakpoint", ["isMobileDevice"]),
    ...mapGetters("user", ["getPermission"]),

    isTableView() {
      return this.viewMode === VIEW_TABLE;
    },

    isTreeView() {
      return this.viewMode === VIEW_TREE;
    },

    resolvedTableHeaders() {
      if (this.headers) {
        return this.headers;
      }

      return buildCategoriesListTableHeaders({
        t: (key) => this.$t(key),
      });
    },

    isMoreThanOnePage() {
      return this.paginationData.lastPage > 1;
    },

    routePage() {
      return Number.parseInt(this.$route.query.page, 10) || 1;
    },

    treeSourceItems() {
      return this.isTreeView ? this.treeItems : this.items;
    },
  },

  watch: {
    items: {
      handler() {
        if (this.isTableView) {
          this.syncLocalCategoriesTreeFromItems();
        }
      },
      immediate: true,
    },

    treeItems() {
      if (this.isTreeView) {
        this.syncLocalCategoriesTreeFromItems();
      }
    },

    treeFilters: {
      async handler() {
        if (this.isTreeView) {
          await this.loadTreeCategories();
        }
      },
      deep: true,
    },

    routePage(newPage) {
      if (!this.isTableView) {
        return;
      }

      if (newPage !== this.paginationData.currentPage) {
        this.$emit("pageChange", newPage);
      }
    },
  },

  methods: {
    ...mapActions("catalogCategories", ["fetchNewCatalogCategories"]),

    completePageChange() {
      if (this.pageChangeResolve) {
        this.pageChangeResolve();
        this.pageChangeResolve = null;
      }
    },

    syncLocalCategoriesTreeFromItems() {
      const items = this.treeSourceItems;

      const buildNode = (item) => {
        const node = { ...item, children: [] };
        const children = items
          .filter((row) => row.parent?.id === item.id)
          .sort(compareCategorySort)
          .map((child) => buildNode(child));

        node.children = children;

        return node;
      };

      this.localCategoriesTree = items
        .filter((item) => !item.parent?.id)
        .sort(compareCategorySort)
        .map((root) => buildNode(root));
    },

    async setViewMode(mode) {
      this.viewMode = mode;

      if (mode === VIEW_TREE) {
        this.syncLocalCategoriesTreeFromItems();
        await this.loadTreeCategories();
      }
    },

    async loadTreeCategories() {
      await this.fetchNewCatalogCategories({
        filters: this.treeFilters,
      });
    },

    onPageChange(page) {
      this.$emit("pageChange", page);

      if (page !== this.routePage) {
        const query = { ...this.$route.query };

        delete query.page;

        const nextQuery = page === 1 ? query : { ...query, page };

        this.$router.push({ name: this.$route.name, query: nextQuery });
      }
    },

    onClickTableRow(row) {
      this.$emit("onClickRow", row.id);
    },

    expandAllBranches() {
      this.$refs.categoriesTreeTable?.expandAllBranches?.();
    },

    collapseAllBranches() {
      this.$refs.categoriesTreeTable?.collapseAllBranches?.();
    },

    async onDragSort() {
      if (!this.getPermission["catalog_category.update"]) {
        return;
      }

      const variables = resolveMoveCatalogCategoryPayload(
        this.treeSourceItems,
        this.localCategoriesTree
      );

      if (!variables) {
        return;
      }

      try {
        await this.$post(moveCatalogCategoryMutation, variables, { delaySuccessNotify: false });
        await this.loadTreeCategories();
        await new Promise((resolve) => {
          this.pageChangeResolve = resolve;
          this.$emit("pageChange", this.routePage);
        });
      } catch {
        this.pageChangeResolve = null;
        this.syncLocalCategoriesTreeFromItems();
      }
    },

    openCategory(id) {
      this.$emit("onClickRow", id);
    },

    onDeleteCategory(id) {
      this.$emit("onDeleteCategory", id);
    },
  },
};
</script>

<style lang="postcss" scoped>
.categories-tree {
  .tree-card-body {
    :deep(.list-item-left-title) {
      margin-left: 0;
    }
  }

  .tree-row-label {
    word-break: break-word;
  }
}
</style>
